import type { GlyphRenderInstruction, TextEngine } from '@meitu/x-design-text';
import type { Bounds } from '@/version-types';
import { MARKER_GAP_EM } from '@/version-types/constants';
import type { TextRenderGraphicListMarker, TextRenderInput } from '../../types';
import { createListMarkerTexts, getPathBounds } from '../../utils';

export type ShapedListMarker = {
    paths: { x: number; y: number; d: string; bounds: Bounds }[];
    width: number;
    baseline: number;
};

/**
 * 正文排版前完成 Text Engine 塑形的列表 marker 事实。
 *
 * `indent` 直接约束正文可用空间；缓存的 path 会在正文 snapshot 产生后补齐最终位置与边界，避免同一
 * marker 为缩进、边界和绘制重复塑形。
 *
 * 图片与 SVG marker 在塑形和几何阶段完全同构——都由素材尺寸而非字体决定占位，只有绘制阶段才需要
 * 区分——因此这里只分字符型与图形型两族。
 */
export type ListMarkerShaping =
    | {
          type: 'character';
          indent: number;
          markers: (ShapedListMarker | null)[];
      }
    | {
          type: 'graphic';
          indent: number;
          width: number;
          height: number;
          numbers: (ShapedListMarker | null)[];
      };

const LOWER_LATIN_MARKER_COUNT = 26;

/**
 * 使用文字引擎把单个列表 marker 塑形成可缓存 path。
 *
 * @param engine - 当前 ready 的文字引擎。
 * @param text - 当前 marker 文案。
 * @param fontFamily - marker 使用的字体族。
 * @param fontSize - marker 使用的字号。
 * @returns 可供缩进、边界与绘制共同消费的 path、宽度和基线；塑形失败时返回 null。
 */
const shapeMarker = (
    engine: TextEngine,
    text: string,
    fontFamily: string,
    fontSize: number,
): ShapedListMarker | null => {
    const buffer = engine.createBuffer({ fontSize });
    let paths: Array<{ x: number; y: number; d: string }>;
    let width: number;
    let baseline: number;
    try {
        buffer.setText(text, { family: fontFamily });
        buffer.layout();
        paths = buffer
            .getRenderInstructions()
            .filter(
                (instruction): instruction is Extract<GlyphRenderInstruction, { kind: 'path' }> =>
                    instruction.kind === 'path',
            )
            .filter((instruction) => instruction.d.trim())
            .map((instruction) => ({ x: instruction.x, y: instruction.y, d: instruction.d }));
        if (paths.length === 0) return null;
        width = buffer.getContentWidth();
        baseline = buffer.getLineMetrics()[0]?.baseline ?? 0;
    } catch {
        return null;
    } finally {
        buffer.dispose();
    }

    // path 边界在塑形后只计算一次；保持无效 path 像旧几何阶段一样向 Renderer 抛出失败。
    return {
        paths: paths.map((path) => ({ ...path, bounds: getPathBounds(path.d) })),
        width,
        baseline,
    };
};

/**
 * 按 marker 文案复用本轮相同的 Text Engine 塑形结果。
 *
 * @param engine - 当前 ready 的文字引擎。
 * @param texts - 按段落顺序生成的 marker 文案。
 * @param fontFamily - marker 使用的字体族。
 * @param fontSize - marker 使用的字号。
 * @returns 与输入文案一一对应的塑形结果；相同文案共享同一结果。
 */
const shapeMarkers = (
    engine: TextEngine,
    texts: readonly string[],
    fontFamily: string,
    fontSize: number,
): (ShapedListMarker | null)[] => {
    const cache = new Map<string, ShapedListMarker | null>();
    return texts.map((text) => {
        if (!cache.has(text)) cache.set(text, text ? shapeMarker(engine, text, fontFamily, fontSize) : null);
        return cache.get(text) ?? null;
    });
};

/**
 * 按当前正文字号同步图形型 marker 的素材尺寸。
 *
 * @param marker - 当前图形型 marker 声明。
 * @param contentFontSize - 当前正文字号。
 * @returns 缩放后的尺寸与缩放系数；素材缺少有效基准字号或宽度时返回 null。
 */
const resolveGraphicMarkerSize = (
    marker: TextRenderGraphicListMarker,
    contentFontSize: number,
): { width: number; height: number; scale: number } | null => {
    if (!marker.baseContentTextSize || marker.baseContentTextSize <= 0 || marker.width < 0) return null;
    const scale =
        (contentFontSize / marker.baseContentTextSize) * (marker.scale && marker.scale > 0 ? marker.scale : 1);
    return { width: marker.width * scale, height: marker.height * scale, scale };
};

/**
 * 在正文排版前一次完成当前输入所需的列表 marker 塑形与缩进测量。
 *
 * @param engine - 当前 ready 的文字引擎。
 * @param input - 已完成字体解析的当前 Text Render Input。
 * @returns 可与随后产生的 snapshot 配对的预排版 marker 事实；未声明 marker 时返回 null。
 */
export const shapeListMarker = (engine: TextEngine, input: TextRenderInput): ListMarkerShaping | null => {
    const marker = input.listMarker;
    if (!marker) return null;
    const gap = input.fontSize * MARKER_GAP_EM;

    if (marker.kind === 'character') {
        const texts = createListMarkerTexts(input);
        const markers = shapeMarkers(engine, texts, input.fontFamily, input.fontSize);
        // lower-latin 保持原有 a-z 最宽值语义；其他编号继续使用末段 marker 决定缩进。
        const indentMarkers =
            marker.style === 'lower-latin' ? markers.slice(0, LOWER_LATIN_MARKER_COUNT) : markers.slice(-1);
        const widths = indentMarkers.flatMap((shaped) => (shaped ? [shaped.width] : []));
        const indent = widths.length > 0 ? Math.max(Math.ceil(Math.max(...widths)), 0) + gap : 0;
        return { type: 'character', indent, markers };
    }

    const size = resolveGraphicMarkerSize(marker, input.fontSize);
    if (!size) return { type: 'graphic', indent: 0, width: 0, height: 0, numbers: [] };
    const { width, height, scale } = size;

    // 自动编号缺少有效尺寸、编号方式或字体时不塑形；素材本身仍保留同步尺寸和列表缩进。
    const numberFontFamily = marker.kind === 'svg' ? marker.numberFontFamily : undefined;
    const numbers = numberFontFamily
        ? shapeMarkers(
              engine,
              width > 0 && height > 0 ? createListMarkerTexts(input) : [],
              numberFontFamily,
              ((marker.kind === 'svg' ? marker.numberFontSize : undefined) ?? input.fontSize) * scale,
          )
        : [];
    return { type: 'graphic', indent: width + gap, width, height, numbers };
};
